import React from 'react'

export type PageTitleAlign = 'left' | 'center' | 'right'

export interface PageTitleOptions {
  align?: PageTitleAlign
  showBreadcrumbs?: boolean
  offsetAppBar?: boolean
}

export interface PageTitleContextValue {
  title: string
  align: PageTitleAlign
  showBreadcrumbs: boolean
  offsetAppBar: boolean
  setPageTitle: (title: string, options?: PageTitleOptions) => void
  clearPageTitle: () => void
}

const PageTitleContext = React.createContext<PageTitleContextValue | undefined>(undefined)
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect

interface PageTitleProviderProps {
  value: PageTitleContextValue
  children: React.ReactNode
}

export const PageTitleProvider: React.FC<PageTitleProviderProps> = ({ value, children }) => {
  return <PageTitleContext.Provider value={value}>{children}</PageTitleContext.Provider>
}

export const usePageTitle = (title?: string, options?: PageTitleOptions) => {
  const context = React.useContext(PageTitleContext)

  if (!context) {
    throw new Error('usePageTitle must be used within a PageTitleProvider')
  }

  const { setPageTitle, clearPageTitle } = context

  useIsomorphicLayoutEffect(() => {
    if (typeof title === 'undefined') {
      return
    }

    setPageTitle(title, options)

    return () => {
      clearPageTitle()
    }
  }, [
    title,
    options?.align,
    options?.showBreadcrumbs,
    options?.offsetAppBar,
    setPageTitle,
    clearPageTitle,
  ])

  return context
}

export default PageTitleContext
